/**
 * Layout: where things sit. Every component here is a box with a semantic job and a few named
 * choices; none takes a `className` or a `style`, and none takes a pixel.
 *
 * The vocabulary, and the one rule behind it:
 *
 * | Component | Job |
 * |---|---|
 * | `Page` | the document: ground, ink, body face, full height |
 * | `Backdrop` | a fixed picture or glow behind the whole page |
 * | `Bar` | the strip across the top — sticky, translucent, page width |
 * | `Main` | the page's one `<main>`, at a measure, with the gutter |
 * | `Band` | one full-bleed section of a long page, with its own air and entrance |
 * | `Wrap` | a measure and a gutter inside a band |
 * | `Stack` / `Row` | a column / a line of things, with a gap from the scale |
 * | `Grid` | a grid of cards, or two halves |
 * | `Split` | a main column beside an aside — the hero, the product page, the checkout |
 * | `Panel` | a raised surface |
 * | `Divider` | a hairline |
 * | `Dock` | a bar that follows a reader once the thing it watches has scrolled away |
 * | `Sentinel` | the bottom of an infinite list |
 *
 * **Spacing comes from the scale, never from a number.** `gap="lg"` is `--a-s-4` everywhere; a page
 * assembled from local guesses (`py-16 sm:py-20` here, `gap-10` there) reads as a hundred local
 * guesses however good each one is, which is the defect `store` described about itself.
 */
import type { CSSProperties, ReactNode, Ref, RefObject } from 'react';

import { cx, mod } from './cx';
import { useNearScreen, useOffscreen, useReveal } from './hooks';

/** A `#rrggbb` colour — the only shape a `Backdrop` field stop is accepted in. */
const HEX = /^#[0-9a-f]{6}$/i;

/** The spacing scale, by name: `xs` 4px · `sm` 8 · `md` 12 · `lg` 16 · `xl` 24 · `2xl` 32 · `3xl` 48. */
export type Gap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/** Text alignment. `responsive` is centred on a phone and starts at the left from 768px. */
export type Align = 'start' | 'center' | 'responsive';

/** Measures, from widest: the page, a reading column, a card, a sheet, a narrow column. */
export type Width = 'page' | 'column' | 'card' | 'sheet' | 'narrow' | 'measure' | 'tight';

type Box = 'div' | 'section' | 'nav' | 'header' | 'footer' | 'aside';

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as { current: T | null }).current = node;
    }
  };
}

/** The document. Ground, ink and the body face, at least one screen tall. */
export function Page({ children }: { children?: ReactNode }) {
  return <div className="a-page">{children}</div>;
}

/**
 * A fixed layer behind the page: the brand's background picture, blurred, under an overlay of the
 * brand's own dark so anything on top stays legible on any photograph — and two soft glows of the
 * accents. With no picture, just the glows over the ground.
 *
 * `field` (2026-10-17) is the brand's PAINTED backdrop instead — the `[centre, edge]` pair from
 * brand-public's `groundStops(brand)`, the same two stops every generator paints with
 * `brand_connector.field`: the primary and a shade of it as one radial falloff, brightest behind the
 * middle. Pure CSS, so nothing is downloaded — no picture, no glows, no second hue. It wins over
 * `image`; a pair that is not two `#rrggbb` colours falls back to the default.
 */
export function Backdrop({
  image,
  field,
}: {
  image?: string | null | undefined;
  field?: readonly string[] | null | undefined;
}) {
  const stops = field && field.length === 2 && field.every((stop) => HEX.test(stop)) ? field : null;
  const picture = !stops && image && image.startsWith('https://') ? image : '';
  if (stops) {
    return (
      <div
        aria-hidden="true"
        className="a-backdrop a-backdrop--field"
        style={{ '--a-field-centre': stops[0], '--a-field-edge': stops[1] } as CSSProperties}
      >
        <div className="a-backdrop__wash" />
      </div>
    );
  }
  return (
    <div aria-hidden="true" className={cx('a-backdrop', picture && 'a-backdrop--picture')}>
      {picture ? (
        // The url is the brand's, from its own CDN; quotes are escaped so it cannot close the url().
        <div
          className="a-backdrop__picture"
          style={{ backgroundImage: `url("${picture.replace(/"/g, '%22')}")` }}
        />
      ) : null}
      <div className="a-backdrop__wash" />
    </div>
  );
}

/**
 * The strip across the top: `start` (the wordmark), `middle` (a search), `end` (actions).
 *
 * The middle slot takes the free width and shrinks first; on a phone it drops to its own row under
 * the bar, because a 200px field with a placeholder in it is not a search field. Sticky and
 * translucent so a page moves under it rather than being cut off — with a real background behind the
 * blur for a browser without `backdrop-filter`.
 */
export function Bar({
  start,
  middle,
  end,
  sticky,
  busy,
}: {
  start?: ReactNode | undefined;
  middle?: ReactNode | undefined;
  end?: ReactNode | undefined;
  sticky?: boolean | undefined;
  /**
   * Work is under way behind the page (a save that closed its form at once): a thin bar in the
   * accent runs along the bar's bottom edge until it lands (owner, 2026-10-21: *"the ui gives the
   * impression of loading faster while there is work taking place"*). Its words are for a screen
   * reader.
   */
  busy?: string | false | null | undefined;
}) {
  return (
    <header className={cx('a-bar', sticky && 'a-bar--sticky')}>
      {busy ? (
        <span className="a-bar__busy" role="progressbar" aria-label={busy} aria-busy="true" />
      ) : null}
      <div className="a-bar__row">
        <div className="a-bar__start">{start}</div>
        {middle ? <div className="a-bar__middle">{middle}</div> : null}
        <div className="a-bar__end">{end}</div>
      </div>
      {middle ? <div className="a-bar__below">{middle}</div> : null}
    </header>
  );
}

/**
 * The page's `<main>`: a measure, the gutter, and vertical air.
 *
 * `center` puts its content in the middle of the remaining height — one card on an otherwise empty
 * page. `safe` pads by the device's safe areas as well, for a page that is the whole screen on a
 * phone (a link page opened from an app's in-app browser).
 */
export function Main({
  children,
  width = 'page',
  pad = 'lg',
  center,
  safe,
}: {
  children?: ReactNode | undefined;
  /** `full` is edge to edge with no gutter: a page made of full-bleed `Band`s that set their own. */
  width?: Width | 'full' | undefined;
  pad?: 'none' | 'md' | 'lg' | 'xl' | 'section' | undefined;
  center?: boolean | undefined;
  safe?: boolean | undefined;
}) {
  return (
    <main
      className={cx(
        'a-main',
        mod('a-main', 'width', width),
        mod('a-main', 'pad', pad),
        center && 'a-main--center',
        safe && 'a-main--safe',
      )}
    >
      {children}
    </main>
  );
}

/**
 * One full-bleed section of a long page.
 *
 * `tone` alternates the ground so each argument sits in its own panel; a page that skips sections
 * should alternate as it SKIPS (see `tones`), or two identical bands end up touching. `reveal`
 * (on by default) brings the band in as it is reached — the first band on a page passes `false`,
 * because the first thing a visitor sees must not fade in. `glow` washes the top in the accent.
 * `tight` is two thirds of the air, for a band holding one line or one button.
 *
 * `ref` reaches the `<section>` — a `Dock` watches the hero band through it.
 */
export function Band({
  children,
  tone = 'base',
  tight,
  reveal = true,
  glow,
  id,
  header,
  ref,
}: {
  children?: ReactNode | undefined;
  tone?: 'base' | 'alt' | undefined;
  tight?: boolean | undefined;
  reveal?: boolean | undefined;
  glow?: boolean | undefined;
  id?: string | undefined;
  /** Render a `<header>` rather than a `<section>` — the first band of a product page. */
  header?: boolean | undefined;
  ref?: Ref<HTMLElement> | undefined;
}) {
  const revealRef = useReveal<HTMLElement>(reveal);
  const Tag = header ? 'header' : 'section';
  return (
    <Tag
      id={id}
      ref={mergeRefs(ref, revealRef)}
      className={cx(
        'a-band',
        mod('a-band', 'tone', tone),
        tight && 'a-band--tight',
        glow && 'a-band--glow',
        reveal && 'a-reveal',
      )}
    >
      {children}
    </Tag>
  );
}

/** A tone that alternates as bands are drawn, so skipping one never leaves two alike touching. */
export function tones(): () => 'base' | 'alt' {
  let alt = false;
  return () => {
    alt = !alt;
    return alt ? 'alt' : 'base';
  };
}

/** A measure and the gutter, centred. */
export function Wrap({
  children,
  width = 'page',
  align,
  top,
}: {
  children?: ReactNode | undefined;
  width?: Width | undefined;
  align?: Align | undefined;
  /** Air above it — a line of chrome (a "back" link) under the bar, outside any band. */
  top?: boolean | undefined;
}) {
  return (
    <div
      className={cx(
        'a-wrap',
        mod('a-wrap', 'width', width),
        mod('a-align', 'is', align),
        top && 'a-wrap--top',
      )}
    >
      {children}
    </div>
  );
}

interface FlowProps {
  children?: ReactNode | undefined;
  gap?: Gap | undefined;
  /** `responsive` centres on a phone and aligns to the start from 768px (`Stack` only). */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline' | 'responsive' | undefined;
  /** Fill the free space of a parent row or column. */
  grow?: boolean | undefined;
  /** Text alignment inside. */
  text?: Align | undefined;
  as?: Box | undefined;
  /** The accessible name of a `nav` or a group. */
  label?: string | undefined;
  /**
   * Marks a loading placeholder: announced as busy with this label, and every `Skeleton` inside
   * pulses together.
   */
  busy?: string | undefined;
}

function flowProps({ as, label, busy }: FlowProps) {
  return {
    Tag: as ?? 'div',
    attributes: {
      ...(label ? { 'aria-label': label } : {}),
      ...(busy ? { role: 'status', 'aria-label': busy, 'aria-busy': true } : {}),
    },
  };
}

/** A column of things with a gap from the scale. */
export function Stack(props: FlowProps & { fill?: boolean }) {
  const { children, gap = 'lg', align, grow, text, busy, fill } = props;
  const { Tag, attributes } = flowProps(props);
  return (
    <Tag
      {...attributes}
      className={cx(
        'a-stack',
        mod('a-gap', 'is', gap),
        mod('a-stack', 'align', align),
        mod('a-align', 'is', text),
        grow && 'a-grow',
        fill && 'a-fill',
        busy && 'a-busy',
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * A line of things with a gap from the scale.
 *
 * `justify="responsive"` centres on a phone and starts at the left from 768px, beside text that does
 * the same. `collapse` stacks the row into a column on a phone.
 */
export function Row(
  props: FlowProps & {
    justify?: 'start' | 'center' | 'end' | 'between' | 'responsive' | undefined;
    wrap?: boolean | undefined;
    collapse?: boolean | undefined;
  },
) {
  const {
    children,
    gap = 'md',
    align = 'center',
    grow,
    text,
    busy,
    justify,
    wrap,
    collapse,
  } = props;
  const { Tag, attributes } = flowProps(props);
  return (
    <Tag
      {...attributes}
      className={cx(
        'a-row',
        mod('a-gap', 'is', gap),
        mod('a-row', 'align', align),
        mod('a-row', 'justify', justify),
        mod('a-align', 'is', text),
        wrap && 'a-row--wrap',
        collapse && 'a-row--collapse',
        grow && 'a-grow',
        busy && 'a-busy',
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * A grid. `cards` is `auto-fill` over a 210px minimum — dense on a desktop, two-up on a phone,
 * without a breakpoint per size, and the same track its skeleton uses so nothing reflows when the
 * answer lands. `halves` is one column on a phone and two from 640px.
 */
export function Grid({
  children,
  kind = 'cards',
  gap,
}: {
  children?: ReactNode | undefined;
  kind?: 'cards' | 'halves' | undefined;
  gap?: Gap | undefined;
}) {
  return (
    <div className={cx('a-grid', mod('a-grid', 'kind', kind), gap && mod('a-gap', 'is', gap))}>
      {children}
    </div>
  );
}

/**
 * A main column beside an aside: the hero (copy, then the cover at the end), the product page (the
 * cover at the start), the checkout (the form, and the summary at the end).
 *
 * `asideFirst` puts the aside FIRST in the document — which is the order a phone reads — while the
 * desktop still places it on `side`. The checkout needs exactly that: somebody who has just pressed a
 * button on a product page must see that the right product arrived before being asked to type, and
 * on a desktop the summary belongs on the right.
 */
export function Split({
  children,
  aside,
  side = 'end',
  width = 'md',
  asideFirst,
  at = 'md',
  align = 'center',
  gap = 'wide',
}: {
  children?: ReactNode | undefined;
  aside: ReactNode;
  side?: 'start' | 'end' | undefined;
  /** The aside's column: `sm` 24rem, `md` 380px, `lg` 460px. */
  width?: 'sm' | 'md' | 'lg' | undefined;
  asideFirst?: boolean | undefined;
  /** Where it becomes two columns: 768px or 1024px. */
  at?: 'md' | 'lg' | undefined;
  align?: 'start' | 'center' | undefined;
  gap?: 'snug' | 'wide' | undefined;
}) {
  const main = <div className="a-split__main">{children}</div>;
  const other = <div className="a-split__aside">{aside}</div>;
  return (
    <div
      className={cx(
        'a-split',
        mod('a-split', 'side', side),
        mod('a-split', 'width', width),
        mod('a-split', 'at', at),
        mod('a-split', 'align', align),
        mod('a-split', 'gap', gap),
      )}
    >
      {asideFirst ? other : main}
      {asideFirst ? main : other}
    </div>
  );
}

/**
 * A raised surface: the brand's surface colour, a hairline, a radius, optionally a shadow.
 *
 * `width` centres it at a measure — a price box, a guarantee, a form card.
 */
export function Panel({
  children,
  pad = 'lg',
  tone = 'surface',
  elevated = true,
  width,
  as = 'section',
}: {
  children?: ReactNode | undefined;
  pad?: 'sm' | 'md' | 'lg' | 'xl' | 'fluid' | undefined;
  tone?: 'surface' | 'alt' | undefined;
  elevated?: boolean | undefined;
  width?: Width | undefined;
  as?: 'section' | 'div' | undefined;
}) {
  const Tag = as;
  return (
    <Tag
      className={cx(
        'a-panel',
        mod('a-panel', 'pad', pad),
        mod('a-panel', 'tone', tone),
        elevated && 'a-panel--elevated',
        width && mod('a-panel', 'width', width),
      )}
    >
      {children}
    </Tag>
  );
}

/** A hairline. `inline` grows to fill a row — the horizon beside a section title. */
export function Divider({ inline, space = 'none' }: { inline?: boolean; space?: Gap }) {
  return inline ? (
    <span aria-hidden="true" className="a-divider a-divider--inline" />
  ) : (
    <hr className={cx('a-divider', mod('a-divider', 'space', space))} />
  );
}

/**
 * The bar that follows a reader down a long page, shown once `watch` has scrolled away — so the
 * decision is reachable from wherever somebody stopped reading.
 *
 * `summary` (the cover, the name, the price) sits beside the action from 640px; on a phone the bar is
 * the action alone, full width. Fixed at the bottom on a phone and at the bottom-right above that.
 * Always mounted and moved out of the way with a transform, because appearing by mounting would make
 * its first paint a layout shift; `inert` while it is away, so neither a keyboard nor a screen reader
 * lands on a control nobody can see. With nothing to watch it stays hidden rather than pinned over a
 * page that may be two screens long.
 */
export function Dock({
  children,
  summary,
  watch,
}: {
  children?: ReactNode | undefined;
  summary?: ReactNode | undefined;
  watch: RefObject<HTMLElement | null>;
}) {
  const shown = useOffscreen(watch);
  return (
    <div className={cx('a-dock', shown && 'a-dock--shown')} inert={!shown} aria-hidden={!shown}>
      {summary ? <div className="a-dock__summary">{summary}</div> : null}
      {children}
    </div>
  );
}

/** Arrive rather than appear — the `Band` entrance, for something that is not a band. */
export function Reveal({ children }: { children?: ReactNode }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className="a-reveal">
      {children}
    </div>
  );
}

/**
 * The bottom of an infinite list: calls `onVisible` as it nears the screen while `active`, and
 * holds `children` (a skeleton of the next page) while it loads.
 */
export function Sentinel({
  active,
  onVisible,
  children,
}: {
  active: boolean;
  onVisible: () => void;
  children?: ReactNode | undefined;
}) {
  const ref = useNearScreen<HTMLDivElement>(active, onVisible);
  return (
    <div ref={ref} className="a-sentinel">
      {children}
    </div>
  );
}
